import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { cn } from 'helpers/classnames';
import type { ImageProps } from 'components/common/Image';
import Image from 'components/common/Image';
import { useCanHover } from 'hooks/useCanHover';
import { useIsMouseOver } from 'hooks/useIsMouseOver';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import { CardMediaMultiImagePreview } from 'components/common/card/CardMediaMultiImagePreview';

/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class */
import css from 'styles/components/common/card/cardMedia.scss';

interface CardMediaProps {
  imageIndex?: number;
  imageClassName?: string;
  isHovered?: boolean;
  mainImage: ImageProps;
  hoverImage?: ImageProps;
  children?: ReactNode;
  className?: string;
  forceLoadIndex?: number;
  imageNoBackground?: boolean;
  fullSizeImage?: boolean;
  onHover?: () => void;
  disableHoverImage?: boolean;
  allImages?: ImageProps[] | null;
  mobileImageIndex?: number;
  styleId?: string | number;
  imageLoadingStrategy?: 'eager' | 'lazy';
  isLcpCandidate?: boolean;
}

const CardMedia = ({
  className,
  imageClassName,
  isHovered,
  mainImage,
  hoverImage,
  imageNoBackground = false,
  fullSizeImage = false,
  onHover,
  disableHoverImage = false,
  children,
  allImages,
  mobileImageIndex = 0,
  styleId,
  imageLoadingStrategy,
  isLcpCandidate = false
}: CardMediaProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isHoverSupported = useCanHover();
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMultiImagePreviewEligible = (allImages?.length ?? 0) > 1;

  // Position-derived hover; armed only for eligible cards on hover-capable devices.
  const isImageHovered = useIsMouseOver(wrapperRef, { enabled: isMultiImagePreviewEligible && isHoverSupported });

  // Eligible multi-image cards use position-derived hover; everything else keeps the prop.
  const hoverActive = isMultiImagePreviewEligible ? isImageHovered : isHovered;

  useEffect(() => {
    if (hoverActive && onHover) {
      onHover();
    }
  }, [hoverActive, onHover]);

  return (
    <div
      ref={wrapperRef}
      className={cn(className, css.imageWrapper, {
        [css.hovered]: !disableHoverImage && hoverActive && hoverImage?.src && !isMultiImagePreviewEligible,
        [css.imageNoBackground]: imageNoBackground
      })}
    >
      {children}
      {isMultiImagePreviewEligible ? (
        <CardMediaMultiImagePreview
          isDesktop={isHoverSupported}
          isHovered={isImageHovered}
          prefersReducedMotion={prefersReducedMotion}
          mobileImageIndex={mobileImageIndex}
          allImages={allImages ?? []}
          mainImage={mainImage}
          styleId={styleId ?? ''}
          imageLoadingStrategy={imageLoadingStrategy}
          isLcpCandidate={isLcpCandidate}
          imageClassName={imageClassName}
          fullSizeImage={fullSizeImage}
        />
      ) : (
        <figure>
          <Image
            className={cn(css.mainImage, { [css.fullSizeImage]: fullSizeImage }, imageClassName)}
            {...mainImage}
            loading={imageLoadingStrategy ?? mainImage.loading}
            fetchPriority={isLcpCandidate ? 'high' : mainImage.fetchPriority}
          />
          {hoverImage && <Image className={cn(css.hoverImage, imageClassName)} {...hoverImage} />}
        </figure>
      )}
    </div>
  );
};

export default CardMedia;
